import { useCallback, useEffect, useState } from "react";

import type { Uuid } from "@/domains/global/identifiers";
import type { LocalWebAudioArtifactChunkRecord } from "@/domains/dexie-db/models/audio-recording/web-artifacts";
import { getDesktopAudioArtifactDurationMs } from "@/modules/electron/desktop-audio-artifact-ref";
import { DesktopAudioArtifactSnapshot, DesktopAudioArtifactState } from "@/modules/electron/types";
import { electronModule } from "@/modules/electron";
import {
  WebAudioArtifactSnapshot,
  WebAudioArtifactState,
} from "@/modules/audio-recording/web-artifacts";
import { useAppStore } from "@/store";
import { AudioRecordingStrategy } from "@/store/audio-recordings";

export type LocalAudioArtifactDebugUnit = {
  id: string;
  detail: string;
};

export type LocalAudioArtifactDebugData = {
  artifactLabel: string;
  artifactStateLabel: string;
  emptyMessage: string;
  filePath: string | null;
  mimeType: string | null;
  totalDurationLabel: string;
  totalDurationMs: number;
  totalSizeBytes: number;
  totalSizeLabel: string;
  unitCount: number;
  unitLabel: string;
  units: LocalAudioArtifactDebugUnit[];
};

type UseLocalAudioArtifactDebugDataArgs = {
  audioRecordingId: Uuid | null;
  recordingStrategy: AudioRecordingStrategy | null;
};

type LoadLocalAudioArtifactDebugDataArgs = {
  audioRecordingId: Uuid;
  recordingStrategy: AudioRecordingStrategy;
  webAudioArtifacts: {
    getAudioArtifactSnapshot(args: {
      audioRecordingId: Uuid;
    }): Promise<WebAudioArtifactSnapshot | null>;
    getAudioArtifactChunks(args: {
      audioRecordingId: Uuid;
    }): Promise<LocalWebAudioArtifactChunkRecord[]>;
  };
};

const formatArtifactDuration = ({ durationMs }: { durationMs: number }): string => {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

const formatArtifactSize = ({ sizeBytes }: { sizeBytes: number }): string => {
  const sizeKb = sizeBytes / 1024;

  if (sizeKb < 1024) {
    return `${sizeKb.toFixed(1)} KB`;
  }

  return `${(sizeKb / 1024).toFixed(2)} MB`;
};

const getArtifactStateLabel = ({
  state,
}: {
  state: DesktopAudioArtifactState | WebAudioArtifactState | null;
}): string => {
  if (state === DesktopAudioArtifactState.Finalized || state === WebAudioArtifactState.Finalized) {
    return "Finalized";
  }

  if (state === DesktopAudioArtifactState.Writing || state === WebAudioArtifactState.Writing) {
    return "Writing";
  }

  return "Missing";
};

const buildWebArtifactDebugData = ({
  chunks,
  snapshot,
}: {
  chunks: LocalWebAudioArtifactChunkRecord[];
  snapshot: WebAudioArtifactSnapshot | null;
}): LocalAudioArtifactDebugData => {
  const totalDurationMs = snapshot?.durationMs ?? 0;
  const totalSizeBytes = snapshot?.sizeBytes ?? 0;

  return {
    artifactLabel: "Web Artifact Chunks",
    artifactStateLabel: getArtifactStateLabel({ state: snapshot?.state ?? null }),
    emptyMessage: "No web-artifact chunks recorded yet.",
    filePath: null,
    mimeType: snapshot?.mimeType ?? null,
    totalDurationLabel: formatArtifactDuration({ durationMs: totalDurationMs }),
    totalDurationMs,
    totalSizeBytes,
    totalSizeLabel: formatArtifactSize({ sizeBytes: totalSizeBytes }),
    unitCount: chunks.length,
    unitLabel: "Chunks",
    units: chunks.map(chunk => ({
      id: chunk.id,
      detail: `#${chunk.chunkIndex.toString().padStart(3, "0")} · ${formatArtifactSize({
        sizeBytes: chunk.byteLength,
      })} · ${formatArtifactDuration({
        durationMs: chunk.durationMs,
      })} · ${chunk.mimeType || "unknown mime"}`,
    })),
  };
};

const buildDesktopArtifactDebugData = ({
  snapshot,
}: {
  snapshot: DesktopAudioArtifactSnapshot | null;
}): LocalAudioArtifactDebugData => {
  const totalDurationMs =
    getDesktopAudioArtifactDurationMs({
      artifact: snapshot,
    }) ?? 0;
  const totalSizeBytes = snapshot?.sizeBytes ?? 0;

  return {
    artifactLabel: "Desktop Artifact",
    artifactStateLabel: getArtifactStateLabel({ state: snapshot?.state ?? null }),
    emptyMessage: "No desktop artifact saved yet.",
    filePath: snapshot?.filePath ?? null,
    mimeType: snapshot?.mimeType ?? null,
    totalDurationLabel: formatArtifactDuration({ durationMs: totalDurationMs }),
    totalDurationMs,
    totalSizeBytes,
    totalSizeLabel: formatArtifactSize({ sizeBytes: totalSizeBytes }),
    unitCount: snapshot ? 1 : 0,
    unitLabel: "Artifacts",
    units: snapshot
      ? [
          {
            id: snapshot.artifactId,
            detail: `${formatArtifactSize({
              sizeBytes: totalSizeBytes,
            })} · ${formatArtifactDuration({
              durationMs: totalDurationMs,
            })} · ${snapshot.mimeType || "unknown mime"}`,
          },
        ]
      : [],
  };
};

const loadLocalAudioArtifactDebugData = async ({
  audioRecordingId,
  recordingStrategy,
  webAudioArtifacts,
}: LoadLocalAudioArtifactDebugDataArgs): Promise<LocalAudioArtifactDebugData> => {
  if (recordingStrategy === AudioRecordingStrategy.DesktopArtifact) {
    const snapshot = await electronModule.audioArtifacts.getAudioArtifactSnapshot({
      audioRecordingId,
    });

    return buildDesktopArtifactDebugData({
      snapshot,
    });
  }

  const [snapshot, chunks] = await Promise.all([
    webAudioArtifacts.getAudioArtifactSnapshot({
      audioRecordingId,
    }),
    webAudioArtifacts.getAudioArtifactChunks({
      audioRecordingId,
    }),
  ]);

  return buildWebArtifactDebugData({
    chunks,
    snapshot,
  });
};

export const useLocalAudioArtifactDebugData = ({
  audioRecordingId,
  recordingStrategy,
}: UseLocalAudioArtifactDebugDataArgs) => {
  const { store } = useAppStore();
  const [data, setData] = useState<LocalAudioArtifactDebugData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refresh = useCallback(
    async ({ showSpinner = true }: { showSpinner?: boolean } = {}) => {
      if (!audioRecordingId || !recordingStrategy) {
        setData(null);
        setError(null);
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }

      if (showSpinner) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }
      setError(null);

      try {
        const nextData = await loadLocalAudioArtifactDebugData({
          audioRecordingId,
          recordingStrategy,
          webAudioArtifacts: store.dexie.repositories.webAudioArtifacts,
        });

        setData(nextData);
      } catch (loadError) {
        const message =
          loadError instanceof Error ? loadError.message : "Failed to load local artifact data.";
        setError(message);
        setData(null);
      } finally {
        if (showSpinner) {
          setIsLoading(false);
        } else {
          setIsRefreshing(false);
        }
      }
    },
    [audioRecordingId, recordingStrategy, store]
  );

  useEffect(() => {
    if (!store.isMemDbReady) {
      return;
    }

    void refresh();
  }, [refresh, store.isMemDbReady]);

  return {
    data,
    error,
    isLoading,
    isRefreshing,
    refresh,
  };
};
